import CONSTANTS from '../Constants';
import api from '../Utils/api';

interface DesignerDTO {
    id: string;
    name: string;
}

interface CategoryDTO {
    id: string;
    name: string;
    garment_count: number;
    sub_categories?: SubCategoryDTO[];
}

interface SubCategoryDTO {
    id: string;
    name: string;
    garment_count: number;
}

export interface ImageDTO {
    created: string;
    created_by: string;
    updated: string;
    updated_by: string;
    id: string;
    content_type: string;
    tag: string;
    url: string;
    size: number;
    height: number;
    width: number;
}

interface VariantDTO {
    color: string;
    size: string;
    price: number;
}

export interface GarmentDTO {
    id: string;
    title: string;
    designer: DesignerDTO;
    categories: CategoryDTO[];
    sub_categories: SubCategoryDTO[];
    description?: string;
    thumbnail: ImageDTO;
    images: ImageDTO[];
    variants: VariantDTO[];
}

interface DesignersResponseDTO {
    designers: DesignerDTO[];
}

interface CategoriesResponseDTO {
    categories: CategoryDTO[];
}

interface GarmentsResponseDTO {
    garments: GarmentV3DTO[];
}

// Garmentv3
export interface GarmentV3DTO {
    created: string;
    updated: string;
    id: string;
    status: string;
    title: string;
    partner_id: string;
    designer: Brand;
    category: string;
    brand: Brand;
    sub_category?: string;
    description?: string;
    available: boolean;
    tags: string[];
    sort: number;
    variants: Variant[];
    rollups: Rollups;
}

export interface Brand {
    id: string;
    name: string;
    sort: number;
    available: boolean;
}

export interface Variant {
    sku: string;
    style: string;
    gender: string;
    color: Color;
    fit: string;
    size_groups?: SizeGroup[];
    price?: Price;
    images: Image[];
    available: boolean;
    sort: number;
    status: string;
    sent_to_ml: string;
}

export interface Color {
    name: string;
    hex: string;
}

export interface SizeGroup {
    sizes: string[];
    size_system: string;
}

export interface Price {
    amount: string;
    currency: string;
}

export interface Image {
    id: string;
    label: string;
    tag?: string;
    url: string;
    content_type: string;
    size: number;
    height: number;
    width: number;
    created: string;
    created_by: string;
}

export interface Rollups {
    sizes: Size[];
    colors: Color[];
    genders: string[];
    styles: string[];
    price_range: PriceRange;
}

export interface Size {
    sizes: string[];
    size_system: string;
}

export interface PriceRange {
    min: string;
    max: string;
}

export interface OutfitGarmentVariant {
    sku: string;
    style: string;
    gender: string;
    color: Color;
    fit: string;
    size_groups: SizeGroup[];
    price?: Price;
    images: Image[];
    available: boolean;
    sort: number;
    status: string;
    image_ingestion_completed?: string;
    cost_context_id?: string;
}

export interface OutfitGarment {
    created: string;
    created_by: string;
    updated: string;
    updated_by: string;
    id: string;
    status: string;
    title: string;
    partner_id: string;
    brand: Brand;
    category: string;
    sub_category?: string;
    description?: string;
    available: boolean;
    tags: string[];
    sort: number;
    variants: OutfitGarmentVariant[];
    rollups: Rollups;
}

export interface OutfitVariantKey {
    style: string;
    color: string;
    fit: string;
    gender: string;
}

export interface OutfitVariantRef {
    partner_id: string;
    garment_id: string;
    variant_key: OutfitVariantKey;
}

export interface Outfit {
    created: string;
    created_by: string;
    updated: string;
    updated_by: string;
    id: string;
    partner_id: string;
    name: string;
    status: string;
    allow_variant_selection: boolean;
    garments: OutfitGarment[];
    variants: OutfitVariantRef[];
}

export interface OutfitsResponseDTO {
    current: number;
    total: number;
    outfits: Outfit[];
}

const PROTEA_BASE_URL = '/v3/protea';

export const getTokenFromStorage = (): Promise<string> => {
    const MAX_WAIT_TIME = 2000;

    return new Promise((resolve, reject) => {
        const startTime = Date.now();

        const checkToken = () => {
            const token = localStorage.getItem(CONSTANTS.LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
            if (token) {
                resolve(token);
            } else if (Date.now() - startTime >= MAX_WAIT_TIME) {
                reject(new Error('Token not found within the allowed time.'));
            } else {
                setTimeout(checkToken, 100);
            }
        };

        checkToken();
    });
};

const garmentService = {
    fetchCategories: async (): Promise<CategoriesResponseDTO> => {
        return api.get(`${PROTEA_BASE_URL}/categories`, await getTokenFromStorage());
    },
    fetchDesigners: async (): Promise<DesignersResponseDTO> => {
        return api.get(`${PROTEA_BASE_URL}/designers`, await getTokenFromStorage());
    },
    fetchAllGarments: async (): Promise<GarmentsResponseDTO> => {
        return api.get(`${PROTEA_BASE_URL}/garments`, await getTokenFromStorage());
    },
    fetchGarment: async (garmentId: string): Promise<GarmentV3DTO> => {
        return api.get(`/v3/garment/${garmentId}`, await getTokenFromStorage());
    },
    fetchOutfitsByGarmentIds: async (garmentIds: string[]): Promise<OutfitsResponseDTO> => {
        const query = garmentIds.map((id) => `garmentID=${encodeURIComponent(id)}`).join('&');
        return api.get(`/v3/garment/outfits?${query}`, await getTokenFromStorage());
    },
    fetchAllOutfits: async (): Promise<OutfitsResponseDTO> => {
        return api.get(`/v3/garment/outfits`, await getTokenFromStorage());
    },
    fetchGarmentsByDesigner: async (designerId: string): Promise<GarmentsResponseDTO> => {
        return api.get(`${PROTEA_BASE_URL}/designer/${designerId}`, await getTokenFromStorage());
    },
    fetchGarmentsByCategory: async (categoryId: string): Promise<GarmentsResponseDTO> => {
        return api.get(
            `${PROTEA_BASE_URL}/garments/category/${categoryId}`,
            await getTokenFromStorage()
        );
    },
    fetchGarmentsSubcategories: async (
        categoryId: string,
        subcategoryId: string
    ): Promise<GarmentsResponseDTO> => {
        return api.get(
            `/garments/category/${categoryId}/${subcategoryId}`,
            await getTokenFromStorage()
        );
    },
};

export default garmentService;
